/* eslint-disable no-useless-escape */
// Sharetribe.jsx

import React, { useEffect, useState } from 'react'
import { useApi } from '../../../../api/api'
import { useSelector } from 'react-redux'
import datasourceDefs from '../../../../api/datasourceConfig'
import {
    ScoopTextField,
    ScoopButton,
    ScoopDialog,
    ScoopLinearProgress,
    ScoopText,
    useToast,
} from '../../../../components/ui'
import styles from './Sharetribe.module.scss'

const bot = datasourceDefs.find((b) => b.name === 'Sharetribe')

export const Sharetribe = ({
    open,
    onClose,
    stepBack,
    type,
    initialDatasetDetails,
    basicConfiguration,
}) => {
    const toast = useToast()
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const { postData } = useApi(bot.API_URL)
    const [url, setUrl] = useState('https://console.sharetribe.com/o/...')
    const [isUrlValid, setIsUrlValid] = useState(true)
    const [isReportVerified, setIsReportVerified] = useState('')
    const [isReportTransactional, setIsReportTransactional] = useState(false)
    const [allowMutipleLoads, setAllowMutipleLoads] = useState(false)
    const [reportName, setReportName] = useState('')
    const [reportDescription, setReportDescription] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [nextStep, setNextStep] = useState(false)
    // set if user is editing vs creating new
    const [isEditMode, setIsEditMode] = useState(false)
    const [inboxName, setInboxName] = useState(null) //used only if inboxName is passed in as part of update call
    const [inboxID, setInboxID] = useState(null) //used only if inboxID is passed in as part of update call

    useEffect(() => {
        if (initialDatasetDetails) {
            setIsEditMode(true)
            setReportDescription(initialDatasetDetails.description)
            setReportName(initialDatasetDetails.label)
            setInboxName(initialDatasetDetails.inboxName)
            setIsReportTransactional(initialDatasetDetails.isTransactional || false)
            setAllowMutipleLoads(initialDatasetDetails.enableMultipleSameDayLoads || false)
            setInboxID(initialDatasetDetails.inboxID)
            setUrl(initialDatasetDetails.reportLink)
        }
    }, [initialDatasetDetails])

    const handleUrlChange = (e) => {
        const newUrl = e.target.value
        setUrl(newUrl)

        // Validate URL
        setIsUrlValid(bot.urlValidation.test(newUrl))
    }

    const runBot = async () => {
        const action = {
            action: 'testRobot',
            service: bot.service,
            reportURL: url,
            userID: userID,
            label: 'Sharetribe TEST',
            inboxName: 'Sharetribe_TEST',
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }

        const result = await postData(action)
        return result
    }

    const handleRunBot = async () => {
        setIsLoading(true)
        try {
            const data = await runBot()
            // TO-DO GABE: fix lambda so returned correctly
            if (data?.status === 'error' && false) {
                toast.error(
                    'Failed to fetch data from Sharetribe. Please make sure you shared with appconnet@scoop-analytics.com'
                )
            } else {
                setIsReportVerified(true)
                toast.success('Sharetribe app connector is connected successfully')
            }
        } catch (error) {
            console.log(error)
        }
        setIsLoading(false)
    }

    function createInboxName(userID, reportName) {
        // Combine userID and reportName with an underscore
        let combinedName = `${userID}_${reportName.substring(0, 30)}`

        // Replace characters not in the allowed list with an underscore
        // Allowed characters: alphanumeric, plus (+), minus (-), equals (=), dot (.), comma (,), underscore (_), colon (:), at (@)
        let scrubbedName = combinedName.replace(/[^a-zA-Z0-9\+\-\=\.\,\_\:\@]/g, '_')

        // Generate a random 3-digit number to help ensure uniqueness
        const randomDigits = Math.floor(100 + Math.random() * 900)

        // Append the random 3-digit number to the scrubbedName
        return `${scrubbedName}_${randomDigits}`
    }

    const createBot = async () => {
        const action = {
            action: 'createRobot',
            inboxName: createInboxName(userID, reportName),
            label: reportName,
            keepOnlyCurrent: basicConfiguration.isMostRecent,
            incremental: basicConfiguration.isIncremental,
            enableMultipleSameDayLoads: basicConfiguration.isMultipleLoads,
            description: reportDescription,
            workspaceID: workspaceID,
            reportURL: url,
            isTransactional: type === 'Transactional',
            userName: userID,
            service: bot.service,
            botUID: bot.botUID,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        const result = await postData(action)
        return result
    }

    const handleCreateBot = () => {
        createBot().then((results) => {
            if (results?.status === 'success')
                toast.success('Sharetribe app connector created successfully')
            else if (results?.status === 'error')
                toast.error(results?.message ?? 'Failed to create Sharetribe app connector')
            setNextStep(false)
            stepBack()
            onClose()
        })
    }

    return (
        <>
            {!nextStep ? (
                <ScoopDialog
                    open={open}
                    title={'Report from Sharetribe'}
                    icon={bot.icon}
                    onClose={onClose}
                    maxWidth="lg"
                    actions={
                        <>
                            <ScoopButton variant="grey" size="sm" onClick={stepBack}>
                                Back
                            </ScoopButton>
                            <ScoopButton
                                size="sm"
                                onClick={() => setNextStep(true)}
                                disabled={isReportVerified !== true}
                            >
                                {' '}
                                I've done this{' '}
                            </ScoopButton>
                        </>
                    }
                >
                    <ScoopText className={styles.headerText}>
                        Share your Sharetribe report with Scoop
                    </ScoopText>
                    <div className={styles.dialogContent}>
                        <div className={styles.stepper}>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>1</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Share your Sharetribe with user <b>{bot.botUID}</b>.
                                    </ScoopText>
                                </div>
                            </div>
                            <div className={styles.step}>
                                <div className={styles.stepHeader}>
                                    <div className={styles.stepIcon}>2</div>
                                    <ScoopText className={styles.stepLabel}>
                                        Paste the entire report URL below
                                    </ScoopText>
                                </div>
                                <div className={styles.stepContent}>
                                    <ScoopTextField value={url} onChange={handleUrlChange} />
                                    {!isUrlValid && (
                                        <ScoopText className={styles.errorText}>
                                            Invalid Sharetribe Reports URL
                                        </ScoopText>
                                    )}
                                    {url && (
                                        <ScoopButton
                                            size="sm"
                                            onClick={handleRunBot}
                                            className={styles.validateButton}
                                        >
                                            Validate Scoop access to your Sharetribe document above
                                        </ScoopButton>
                                    )}
                                </div>
                            </div>

                            {isLoading && (
                                <ScoopLinearProgress
                                    variant="indeterminate"
                                    className={styles.validateButton}
                                />
                            )}
                        </div>
                    </div>
                </ScoopDialog>
            ) : (
                <ScoopDialog
                    open={open}
                    title={'New Report from Sharetribe'}
                    icon={bot.icon}
                    onClose={onClose}
                    actions={
                        <>
                            <ScoopButton
                                variant="grey"
                                size="sm"
                                onClick={() => setNextStep(false)}
                            >
                                Back
                            </ScoopButton>
                            <ScoopButton size="sm" onClick={() => handleCreateBot()}>
                                {' '}
                                Create Report{' '}
                            </ScoopButton>
                        </>
                    }
                    className={styles.dialogNarrow}
                >
                    <ScoopTextField
                        label={'Report name'}
                        onChange={(event) => {
                            setReportName(event.target.value)
                            setReportDescription(event.target.value)
                        }}
                        value={reportName}
                    />
                </ScoopDialog>
            )}
        </>
    )
}
